/**
 * Material Behavior Registry
 * 
 * Central registry for tracking active material behaviors (water, lava, video).
 * Allows the debug UI to enumerate and edit material parameters at runtime.
 */

import type { AbstractMesh } from '@babylonjs/core';

export interface MaterialBehaviorEntry {
  /** Entity ID */
  entityId: string;
  /** Mesh the material is applied to */
  mesh: AbstractMesh;
  /** Behavior ID (e.g., 'material:water', 'material:lava', 'material:video') */
  behaviorId: string;
  /** Display name for UI */
  displayName: string;
  /** Current parameter values */
  getParams: () => Record<string, number | string | boolean>;
  /** Update a single parameter */
  setParam: (name: string, value: number | string | boolean) => void;
}

// Central registry of all active material behaviors
const activeMaterials = new Map<string, MaterialBehaviorEntry>();

/**
 * Register a material behavior instance
 */
export function registerMaterialBehavior(entry: MaterialBehaviorEntry): void {
  activeMaterials.set(entry.entityId, entry);
}

/**
 * Unregister a material behavior instance
 */
export function unregisterMaterialBehavior(entityId: string): void {
  activeMaterials.delete(entityId);
}

/**
 * Get all active material behaviors
 */
export function getActiveMaterialBehaviors(): MaterialBehaviorEntry[] {
  return Array.from(activeMaterials.values());
}

/**
 * Get a specific material behavior by entity ID
 */
export function getMaterialBehavior(entityId: string): MaterialBehaviorEntry | undefined {
  return activeMaterials.get(entityId);
}

/**
 * Get a material behavior by mesh name
 */
export function getMaterialBehaviorByMeshName(meshName: string): MaterialBehaviorEntry | undefined {
  for (const entry of activeMaterials.values()) {
    if (entry.mesh.name === meshName) {
      return entry;
    }
  }
  return undefined;
}
